import Badge from '@/common/components/Badge'
import Box from '@/common/components/Box'
import { CurrencyTextField } from '@/common/components/CurrencyTextField'
import Modal from '@/common/components/Modal'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import { UnitTextField } from '@/common/components/UnitTextField'
import { ProductCategoryData } from '@/common/types/productCategory.type'
import { ContractProductCategoryAppendProduct } from '@/features/publicCompanyLeadsFunnel/components/ContractProductCategoryAppendProduct'
import { ContractProductCategoryProductItem } from '@/features/publicCompanyLeadsFunnel/components/ContractProductCategoryProductItem'
import { useCreatePublicCompanyLeadContractProduct } from '@/features/publicCompanyLeadsFunnel/hooks/useCreatePublicCompanyLeadContractProduct'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { BoxesIcon } from 'lucide-react'
import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { Controller, FormProvider, SubmitHandler, useFieldArray, useForm } from 'react-hook-form'
import {
  contractProductCategorySchema,
  PublicCompanyLeadsFunnelContractProductCategoryValidationSchema,
} from '../schemas/update.schema'
import { PublicCompanyLeadData } from '../types/publicCompanyLead'

interface CreateContractProductCategoryModalProps {
  lead?: PublicCompanyLeadData
  productCategory: ProductCategoryData | undefined
  setProductCategory: Dispatch<SetStateAction<ProductCategoryData | undefined>>
}

export const CreateContractProductCategoryModal = ({
  lead,
  productCategory,
  setProductCategory,
}: CreateContractProductCategoryModalProps) => {
  const queryClient = useQueryClient()

  const [messages, setMessages] = useState<string[] | boolean>(false)

  const { mutate: createPublicCompanyLeadContract } = useCreatePublicCompanyLeadContractProduct({
    publicCompanyLead: lead,
    productCategory,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['publicCompanyLead', lead?.id] })

      handleClose()

      reset()
    },
    onError: (error) => {
      setMessages(error.response?.data.errors.base || ['Ocorreu um erro ao atualizar'])
    },
  })

  const formMethods = useForm<PublicCompanyLeadsFunnelContractProductCategoryValidationSchema>({
    resolver: zodResolver(contractProductCategorySchema),
  })

  const {
    handleSubmit,
    control,
    setValue,
    reset,
    formState: { errors },
  } = formMethods

  const {
    fields: contractProducts,
    append: appendContractProduct,
    remove: removeContractProduct,
  } = useFieldArray({
    control,
    name: `contract_product_category_products`,
  })

  const onSubmit: SubmitHandler<PublicCompanyLeadsFunnelContractProductCategoryValidationSchema> = (form) =>
    createPublicCompanyLeadContract(form)

  const handleClose = () => {
    reset()
    contractProducts.forEach((_, index) => removeContractProduct(index))
    setProductCategory(undefined)
  }

  useEffect(() => {
    if (productCategory?.id) {
      setValue('id', productCategory.id)
      setValue('product_category', productCategory)
      setValue('global_value_in_currency', undefined)
      setValue('global_value_in_unit_for_consumption', undefined)
      setValue('require_product_credit_quota', lead?.contract?.require_product_credit_quota || false)
      setValue('contract_product_category_products', [])
    }
  }, [productCategory?.id])

  return (
    <>
      <Modal
        isOpen={!!productCategory?.id}
        setIsOpen={handleClose}
        dataTestid='confirm_contract_product_category_modal_button'
        useCancellableFooter
        onConfirm={handleSubmit(onSubmit)}
        onCancel={handleClose}
        cancelButtonClassName="bg-white-100"
        confirmButtonClassName="bg-primary-blue"
        className="min-w-[640px]"
        title={`Detalhando ${productCategory?.display_name}`}
        body={
          <FormProvider {...formMethods}>
            <form>
              <Box className="flex flex-col gap-4 max-h-[480px] min-h-[320px] overflow-auto text-white-800">
                <Badge
                  Icon={BoxesIcon}
                  iconProps={{ className: 'size-6', strokeWidth: 1 }}
                  text={productCategory?.display_name || ''}
                  className="rounded-sm flex bg-neutral-op-8 w-fit flex-row-reverse !p-0 pe-2 gap-0"
                  overwriteClassName
                  contentClassName="px-3 py-2"
                />

                {!!lead?.contract?.require_product_credit_quota && (
                  <div className="flex gap-2">
                    <Controller
                      control={control}
                      name="global_value_in_currency"
                      render={({ field }) => (
                        <CurrencyTextField
                          dataTestid="contract_product_category_value_in_currency_input"
                          label="Cota total em valor"
                          markAsRequired
                          className="w-1/2"
                          {...field}
                          errorMessage={errors?.global_value_in_currency?.message}
                          disabled={false}
                        />
                      )}
                    />

                    <Controller
                      control={control}
                      name="global_value_in_unit_for_consumption"
                      render={({ field }) => (
                        <UnitTextField
                          dataTestid="contract_product_category_value_in_unit_for_consumption_input"
                          label="Cota total em quantidade"
                          markAsRequired
                          className="w-1/2"
                          decimals={2}
                          {...field}
                          errorMessage={errors?.global_value_in_unit_for_consumption?.message}
                          disabled={false}
                        />
                      )}
                    />
                  </div>
                )}

                <ContractProductCategoryAppendProduct
                  productCategoryId={productCategory?.id}
                  contractProducts={contractProducts}
                  appendContractProduct={appendContractProduct}
                />

                {contractProducts?.map((contractProduct, index) => (
                  <ContractProductCategoryProductItem
                    key={contractProduct.product?.id}
                    lead={lead}
                    contractProductIndex={index}
                    contractProduct={contractProduct}
                    removeContractProduct={removeContractProduct}
                  />
                ))}
              </Box>
            </form>
          </FormProvider>
        }
      />

      {messages && <ModalMessageSimple messages={messages} setMessages={setMessages} isError />}
    </>
  )
}
